/** live-transcriber's AssemblyAI clips: 16 kHz s16le. */
const DEFAULT_SAMPLE_RATE = 16000;

/**
 * HITL-1786: plays a card's clip, separate from the live guest audio worklet
 * (`asrcAudio.ts`), so a replay never disturbs or is disturbed by the live
 * stream's catch-up logic. One clip at a time: a new play stops the previous
 * one.
 *
 * Through one HTMLAudioElement, not Web Audio: the clip plays faster than
 * real time (the request's `playback_rate`, 1.25 by default), and an
 * AudioBufferSourceNode's `playbackRate` raises the pitch with the speed,
 * where a media element keeps it (`preservesPitch`). The PCM is wrapped in a
 * WAV blob and played from an object URL, revoked when the next clip replaces
 * it or playback stops.
 *
 * Autoplay: Chrome lets media start without a click once the page has had a
 * user gesture (the lobby's cue already plays that way). A `play()` the
 * browser refuses resolves null: the caller falls back to the Play button.
 */

export interface ClipPlayback {
  /** Local wall-clock ms when playback started. */
  startedAt: number;
}

type AudioFactory = () => HTMLAudioElement;

export interface ObjectUrls {
  create: (blob: Blob) => string;
  revoke: (url: string) => void;
}

const defaultAudioFactory: AudioFactory = () => new Audio();

const defaultUrls: ObjectUrls = {
  create: (blob) => URL.createObjectURL(blob),
  revoke: (url) => URL.revokeObjectURL(url),
};

const WAV_HEADER_BYTES = 44;

/** 16-bit mono PCM to the bytes of a WAV file (RIFF header + samples, little-endian). */
export const encodeWavBytes = (
  samples: Int16Array,
  sampleRate: number
): Uint8Array => {
  const dataBytes = samples.length * 2;
  const bytes = new Uint8Array(WAV_HEADER_BYTES + dataBytes);
  const view = new DataView(bytes.buffer);
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) {
      view.setUint8(offset + i, text.charCodeAt(i));
    }
  };
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, 'data');
  view.setUint32(40, dataBytes, true);
  for (let i = 0; i < samples.length; i += 1) {
    view.setInt16(WAV_HEADER_BYTES + i * 2, samples[i], true);
  }
  return bytes;
};

export const encodeWav = (samples: Int16Array, sampleRate: number): Blob =>
  new Blob([encodeWavBytes(samples, sampleRate)], { type: 'audio/wav' });

/** Keeps the voice's pitch when the speed changes, prefixed props included. */
const preservePitch = (audio: HTMLAudioElement) => {
  const media = audio as any;
  media.preservesPitch = true;
  if ('webkitPreservesPitch' in media) media.webkitPreservesPitch = true;
  if ('mozPreservesPitch' in media) media.mozPreservesPitch = true;
};

export class ClipPlayer {
  private audio?: HTMLAudioElement;

  private url?: string;

  /**
   * Bumped by every `stop()`. `play()` awaits the element's `play()`; a stop
   * during that await (the card ended: deadline, answer, order) must win, or
   * the clip would play on with nothing left to stop it.
   */
  private stops = 0;

  /** Bumped by every `play()`: only the newest one may report a start. */
  private plays = 0;

  constructor(
    private readonly createAudio: AudioFactory = defaultAudioFactory,
    private readonly now: () => number = () => Date.now(),
    private readonly urls: ObjectUrls = defaultUrls
  ) {}

  private element(): HTMLAudioElement {
    if (!this.audio) this.audio = this.createAudio();
    return this.audio;
  }

  stop() {
    this.stops += 1;
    this.release();
  }

  /** Pauses and lets go of the current clip's URL. */
  private release() {
    const url = this.url;
    this.url = undefined;
    if (this.audio) {
      try {
        this.audio.pause();
      } catch {
        // nothing loaded
      }
    }
    if (url) this.urls.revoke(url);
  }

  async play(
    samples: Int16Array,
    sampleRate: number = DEFAULT_SAMPLE_RATE,
    rate = 1
  ): Promise<ClipPlayback | null> {
    if (!samples.length) {
      return null;
    }
    this.plays += 1;
    const playId = this.plays;
    const stopsAtStart = this.stops;
    let audio: HTMLAudioElement;
    try {
      audio = this.element();
      // One clip at a time.
      this.release();
      const url = this.urls.create(encodeWav(samples, sampleRate));
      this.url = url;
      audio.src = url;
      preservePitch(audio);
      // Loading a new src resets playbackRate to defaultPlaybackRate.
      audio.defaultPlaybackRate = rate;
      audio.playbackRate = rate;
      // Refused (no gesture yet) or interrupted: nothing played, so it never
      // counts as heard.
      await audio.play();
    } catch {
      return null;
    }
    if (this.plays !== playId) {
      // A newer play() took the element over; it reports its own start.
      return null;
    }
    if (this.stops !== stopsAtStart) {
      this.release();
      return null;
    }
    return { startedAt: this.now() };
  }

  async close() {
    this.stop();
    this.audio = undefined;
  }
}
